import styled, { keyframes } from "styled-components";
import { useState, useRef, useEffect } from "react";
import { useAnnouncementsState } from 'hooks/useAnnouncementsState';
import { useAnnouncementHandlers } from './useAnnouncementHandlers';
import { useUserState } from 'hooks/useUserState';
import AddAnnouncement from './add-announcement';

const Announcements = ({ 
  isHidden, 
  onExpand, 
  onCollapse, 
  showAddAnnouncement,
  onCloseAdd,
  isExpanded 
}) => {
  const containerRef = useRef(null);
  const { announcements, updateAnnouncement, setUpdateCounter } = useAnnouncementsState();
  const { organizations } = useUserState();
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const {
    editingAnnouncement,
    tempContent,
    showDetails,
    expandedId,
    handleExpandClick,
    handleAnnouncementClick,
    handleEditClick,
    handleAddAnnouncementClick,
    handleCancel,
    updateContent,
  } = useAnnouncementHandlers({
    onExpand,
    onCollapse,
    updateAnnouncement,
    organizations,
    containerRef,
    announcements,
    setUpdateCounter,
  });

  const handleSave = async (announcementData) => {
    try {
      setIsSaving(true);
      const result = await updateAnnouncement(announcementData);
      
      if (result.error) {
        throw new Error(result.error.message || 'Failed to save announcement');
      }

      if (!result.data || !result.data.id) {
        throw new Error('Invalid response data');
      }
      
      setJustSaved(true);
      
      // Only close if it's a new announcement
      if (showAddAnnouncement && announcementData.id === 'new') {
        setTimeout(() => {
          onCloseAdd();
        }, 1000);
      }

      setTimeout(() => {
        setIsSaving(false);
        setJustSaved(false);
      }, 1000);

      return result;
    } catch (error) {
      setIsSaving(false);
      console.error('Failed to save announcement:', error);
      alert(error.message || 'Failed to save announcement. Please try again.');
      return { data: null, error };
    }
  };

  const handleDelete = async (announcement) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }
    
    try {
      setIsSaving(true);
      const result = await updateAnnouncement({
        id: announcement.id,
        organization_id: announcement.organization_id,
        _delete: true
      });
      
      if (result.error) throw result.error;
      
      if (showAddAnnouncement) {
        onCloseAdd();
      }
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      alert('Failed to delete announcement. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if user is admin of any organization
  const isAdmin = organizations?.admin?.length > 0;

  // Check if user is admin of the announcement's organization
  const isAdminOfOrg = (organizationId) => {
    return organizations?.admin?.some(org => org.organizationId === organizationId);
  };

  // Filter announcements based on draft status and admin rights
  const filteredAnnouncements = (announcements || []).filter(announcement => {
    // If it's not a draft, show it to everyone
    if (!announcement.content?.isDraft) {
      return true;
    }
    
    // If it's a draft, only show it to admins of the organization
    return isAdminOfOrg(announcement.organization_id);
  });

  if (showAddAnnouncement) {
    return (
      <AddAnnouncement 
        onClose={onCloseAdd}
        editingAnnouncement={{
          id: 'new',
          organization_id: organizations?.admin?.[0]?.organizationId,
          type: 'update',
          tempContent: tempContent
        }}
        onSave={handleSave}
        onDelete={handleDelete}
        isSaving={isSaving}
        justSaved={justSaved}
        updateContent={updateContent}
      />
    );
  }

  return (
    <AnnouncementContainer
      ref={containerRef}
      $isExpanded={expandedId !== null}
      $isHidden={isHidden}
    >
      {filteredAnnouncements.map((announcement) => (
        <AnnouncementCard
          onClick={() => handleExpandClick(announcement.id)}
          key={announcement.id}
          $isExpanded={expandedId === announcement.id}
          $hide={expandedId !== null && expandedId !== announcement.id}
          $isDraft={announcement.content?.isDraft}
        >
          <Header
            $isExpanded={expandedId === announcement.id}
          >
            <StatusDot $type={announcement.type} />
            {editingAnnouncement?.id === announcement.id ? (
              <EditableTitle
                value={tempContent.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
                placeholder="Title"
              />
            ) : (
              <Title>{announcement.title}</Title>
            )}
            {announcement.content?.isDraft && (
              <DraftBadge>Draft</DraftBadge>
            )}
            <DaysAgo>{announcement.date}</DaysAgo>
            {expandedId === announcement.id && !editingAnnouncement && isAdminOfOrg(announcement.organization_id) && (
              <EditButton onClick={(e) => handleEditClick(e, announcement)}>
                Edit
              </EditButton>
            )}
          </Header>
          <ContentWrapper $isExpanded={expandedId === announcement.id}>
            {editingAnnouncement?.id === announcement.id ? (
              <>
                <EditableDescription
                  value={tempContent.description || ''}
                  onChange={(e) => updateContent('description', e.target.value)}
                  placeholder="Description"
                />
                <AddAnnouncement 
                  onClose={handleCancel}
                  editingAnnouncement={{
                    ...editingAnnouncement,
                    title: tempContent.title || editingAnnouncement.title,
                    description: tempContent.description || editingAnnouncement.description,
                    content: tempContent.content || editingAnnouncement.content
                  }}
                  onSave={handleSave}
                  onDelete={handleDelete}
                  isSaving={isSaving}
                  justSaved={justSaved}
                  updateContent={updateContent}
                />
              </>
            ) : (
              <>
                <Description
                  $isExpanded={expandedId === announcement.id}
                >{announcement.description}
                </Description>
                {expandedId === announcement.id ? (
                  <BodyContent>
                    <div dangerouslySetInnerHTML={{ __html: announcement.content?.html || announcement.content?.text || '' }}></div>
                  </BodyContent>
                ) : (
                  <ExpandButton onClick={() => handleExpandClick(announcement.id)}>
                    Expand
                  </ExpandButton>
                )}
              </>
            )}
          </ContentWrapper>
        </AnnouncementCard>
      ))}
      {expandedId !== null && !editingAnnouncement && (
        <CollapseButton onClick={() => handleExpandClick(expandedId)}>
          <span>👈🏽 Back to Announcements</span>
        </CollapseButton>
      )}
    </AnnouncementContainer>
  );
};


const fadeInSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInSlideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnnouncementContainer = styled.div.attrs({
  className: 'AnnouncementContainer'
})`
  display: ${({ $isHidden }) => $isHidden ? 'none' : 'flex'};
  flex-direction: column;
  gap: 8px;
  background: white;
  padding: 11px 10px 11px 10px;
  box-sizing: border-box;
  border-radius: 5px;
  height: 100%;
  overflow-y: auto;
  width: 100%;
  transition: max-height 0.3s ease-out;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #e5e5e5;
    border-radius: 20px;
  }
`;

const AnnouncementCard = styled.div.attrs({
  className: 'AnnouncementCard'
})`
  display: flex;
  align-items: flex-start;
  background-color: ${({ $isExpanded, $isDraft }) => 
    $isDraft 
      ? ($isExpanded ? '#fffbf0' : '#fff8e6') 
      : ($isExpanded ? 'white' : '#f5f5f5')
  };
  padding: ${({ $isExpanded }) => $isExpanded ? '0px' : '10px 13px 18px 13px'};
  border-radius: 5px;
  position: relative;
  flex-direction: column;
  gap: 10px;
  animation: ${fadeInSlideDown} 0.5s ease-out forwards;
  will-change: transform, opacity;
  // transition: all 0.3s ease-out;
  
  ${({ $isExpanded }) => $isExpanded && `
    flex: 1;
    height: 100%;
    overflow-y: auto;
        margin-right: -10px;
    padding-right: 10px;
  `}
  
  ${({ $hide }) => $hide && `
    display: none;
  `}
  &:hover {
    cursor: default;
    .ExpandButton {
      color: #1CCCA4;
      transition: all 0.3s ease-out;
    }
  }
`;

const Header = styled.div.attrs({
  className: 'Header'
})`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 13px;
  ${({ $isExpanded }) => $isExpanded && `
    background: #f7f7f7;
    padding: 6px 13px;
    box-sizing: border-box;
    border-radius: 3px;
    margin-bottom: 0px;
  `}
`;

const Title = styled.h3.attrs({
  className: 'Title'
})`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 14px;
  margin-top: 2px;
`;

const StatusDot = styled.div.attrs({
  className: 'StatusDot'
})`
  min-width: 11px;
  min-height: 11px;
  border-radius: 60%;
  background-color: ${({ $type }) => {
    switch ($type) {
      case "kickoff":
        return "#1CCCA4";
      case "update":
        return "#a855f7";
      case "resource":
        return "#ec4899";
      default:
        return "#gray";
    }
  }};
`;

const DraftBadge = styled.span.attrs({
  className: 'DraftBadge'
})`
  background-color: #f59e0b;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
`;

const DaysAgo = styled.span.attrs({
  className: 'DaysAgo'
})`
  color: #000;
  font-family: pt sans;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  letter-spacing: -0.48px;
  margin-left: auto;
  margin-top: 3px;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 11px;
  font-weight: 900;
  padding: 5px 10px;
  cursor: pointer;
  text-decoration: underline;
  margin-left: auto;
  font-family: "PT Sans";

  &:hover {
    color: black;
  }
`;

const ContentWrapper = styled.div.attrs({
  className: 'ContentWrapper'
})`
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: ${({ $isExpanded }) => $isExpanded ? '0px' : '5px'};
  flex: 1;
  width: 100%;
  box-sizing: border-box;
`;

const Description = styled.span.attrs({
  className: 'Description'
})`
  background: #a855f7;
  padding: 5px 10px 10px 10px;
  border-radius: 3px;
  color: white;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  margin: 0px -10px 0px -10px;
  ${({ $isExpanded }) => $isExpanded && `
    margin: 0px 3px 3px 1px;
  `}
  &:hover {
    background: #8b45e6;
  }
`;

const ExpandButton = styled.div.attrs({
  className: 'ExpandButton'
})`
  color: #999;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-decoration-line: underline;
  cursor: pointer;
`;

const CollapseButton = styled.div.attrs({
  className: 'CollapseButton'
})`
  bottom: 0;
  left: 0;
  right: 0;
  text-decoration: none;
  font-size: 14px;
  background: black;
  color: white !important;
  box-sizing: border-box;
  cursor: pointer;
  opacity: 0;
  animation: ${fadeInSlideUp} 0.3s ease-out forwards;
  animation-delay: 0.2s;
  z-index: 100;
  height: initial;
  border-radius: 25px;
  padding: 3px 25px 5px 15px;
  width: fit-content;

  &:hover {
    background: #333;
  }
`;

const EditableTitle = styled.textarea.attrs({
  className: 'EditableTitle'
})`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  font-family: "PT Sans";
  line-height: 14px;
  margin-top: 2px;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  resize: none;
  overflow: hidden;
  min-height: 18px;
  height: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  align-content: center;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: #999;
  }
`;

const EditableDescription = styled.textarea`
  border: none;
  padding: 5px 10px 10px 10px;
  box-sizing: border-box;
  font-size: 12px;
  font-family: "PT Sans";
  line-height: 17px;
  resize: none;
  background: #a855f7;
  color: white;
  font-weight: 500;
  border-radius: 5px;
  min-height: 50px;

  &:focus {
    outline: none;
    background: #8b45e6;
  }
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const BodyContent = styled.div.attrs({
  className: 'BodyContent'
})`
  color: #333;
  font-size: 13.5px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  line-height: 18px;
  padding: 8px 10px 20px 10px;
  white-space: pre-wrap;
  font-weight: 400;
  animation: ${fadeInSlideUp} 0.3s ease-out forwards;
  p {
    font-size: 13.5px;
    line-height: 18px;
    margin: 0px;
  }
`;

export default Announcements;